import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { 
  CheckCircle2, 
  Clock, 
  AlertTriangle,
  TrendingUp
} from "lucide-react";
import { cn } from "@/lib/utils";

interface ActionsHeaderProps {
  stats: {
    total: number;
    completed: number;
    urgent: number;
    thisWeek: number;
    expired: number;
  };
}

export function ActionsHeader({ stats }: ActionsHeaderProps) {
  const completionRate = (stats.total + stats.completed) > 0 
    ? Math.min(100, Math.round((stats.completed / (stats.total + stats.completed)) * 100))
    : 100;
  
  const urgencyScore = Math.max(0, 100 - (stats.urgent * 15) - (stats.expired * 25));

  return (
    <div className="space-y-4">
      {/* Main Score Card */}
      <Card className="overflow-hidden border-0 bg-gradient-to-br from-primary/5 via-background to-accent/5">
        <CardContent className="p-6">
          <div className="flex items-center gap-6">
            {/* Circular Score */}
            <div className="relative w-24 h-24 flex-shrink-0">
              <svg className="w-full h-full transform -rotate-90">
                <circle
                  cx="48"
                  cy="48"
                  r="42"
                  strokeWidth="8"
                  className="fill-none stroke-muted"
                />
                <motion.circle
                  cx="48"
                  cy="48"
                  r="42"
                  strokeWidth="8"
                  strokeLinecap="round"
                  className={cn(
                    "fill-none",
                    urgencyScore >= 80 ? "stroke-success" :
                    urgencyScore >= 50 ? "stroke-warning" :
                    "stroke-destructive"
                  )}
                  initial={{ strokeDasharray: "0 264" }}
                  animate={{ strokeDasharray: `${(urgencyScore / 100) * 264} 264` }}
                  transition={{ duration: 1, ease: "easeOut" }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <motion.span 
                  className="text-2xl font-bold"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  {urgencyScore}%
                </motion.span>
                <span className="text-[10px] text-muted-foreground">Score</span>
              </div>
            </div>

            {/* Stats */}
            <div className="flex-1 space-y-3">
              <div>
                <h2 className="text-lg font-semibold">
                  {stats.total === 0 ? "Tout est en ordre !" : 
                   stats.urgent > 0 ? "Actions urgentes en attente" :
                   "Quelques actions à prévoir"}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {stats.total} action{stats.total !== 1 ? 's' : ''} restante{stats.total !== 1 ? 's' : ''}
                </p>
              </div>

              {/* Progress bar */}
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Progression ce mois</span>
                  <span>{stats.completed} terminée{stats.completed !== 1 ? 's' : ''}</span>
                </div>
                <Progress value={completionRate} className="h-2" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card className={cn(
          "border-l-4",
          stats.expired > 0 ? "border-l-destructive" : "border-l-muted"
        )}>
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className={cn(
                "w-4 h-4",
                stats.expired > 0 ? "text-destructive" : "text-muted-foreground"
              )} />
              <div>
                <p className="text-lg font-bold">{stats.expired}</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">En retard</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={cn(
          "border-l-4",
          stats.urgent > 0 ? "border-l-warning" : "border-l-muted"
        )}>
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <Clock className={cn(
                "w-4 h-4",
                stats.urgent > 0 ? "text-warning" : "text-muted-foreground"
              )} />
              <div>
                <p className="text-lg font-bold">{stats.thisWeek}</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Cette semaine</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-success">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-success" />
              <div>
                <p className="text-lg font-bold">{stats.completed}</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Ce mois</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
